import { useMemo } from 'react'
import { Table, Thead, Tbody, Tr, Td, Th, TableScrollHost } from '@/common/ui/Table'
import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import type { BrandComparisonBrandData } from '../../model/useCompanyStatsBrandComparison'
import { mergeBlockCriteria, type MergedCriteriaRow } from '../../lib/brandComparison'
import { exportRowsToExcel } from '../../lib/exportExcel'
import { BLOCK_TITLE_TEXT_CLASS, BLOCK_TOP_BORDER } from '../../lib/scoreColors'
import { getBlockTitle, getCriteriaLabel } from '../../lib/labels'
import { BrandComparisonDeltaBadge } from './BrandComparisonDeltaBadge'
import { CompanyStatsExcelExportButton } from '../CompanyStatsExcelExportButton'

type CompanyStatsBrandComparisonCriteriaTablesProps = {
    brandA: BrandComparisonBrandData
    brandB: BrandComparisonBrandData
}

const formatGapForExport = (row: MergedCriteriaRow): string => {
    if (row.delta.sign === 'equal') return '≈'
    const prefix = row.delta.raw > 0 ? '+' : '-'
    return `${prefix}${Math.round(row.delta.value)}%`
}

const formatLeaderForExport = (row: MergedCriteriaRow): string => (row.leader.type === 'equal' ? '≈' : row.leader.name)

const exportBlockCriteriaToExcel = (
    rows: MergedCriteriaRow[],
    sectionTitle: string,
    brandAName: string,
    brandBName: string,
    blockType: string
) => {
    exportRowsToExcel(
        rows,
        [
            { header: 'Критерий', getValue: row => row.label },
            { header: 'Разрыв', getValue: row => formatGapForExport(row) },
            { header: brandAName, getValue: row => `${Math.round(row.brandAPercent)}%` },
            { header: brandBName, getValue: row => `${Math.round(row.brandBPercent)}%` },
            { header: 'Лидер', getValue: row => formatLeaderForExport(row) },
        ],
        `brand_comparison_${blockType}`,
        sectionTitle.slice(0, 31)
    )
}

export const CompanyStatsBrandComparisonCriteriaTables = ({
    brandA,
    brandB,
}: CompanyStatsBrandComparisonCriteriaTablesProps) => {
    const mergedBlocks = useMemo(
        () =>
            mergeBlockCriteria(brandA.scores.blocks, brandB.scores.blocks, brandA.name, brandB.name, criteria =>
                getCriteriaLabel(criteria.title, criteria.title_type, criteria.criteria_name)
            ),
        [brandA.name, brandA.scores.blocks, brandB.name, brandB.scores.blocks]
    )

    if (mergedBlocks.length === 0) return null

    return (
        <div className='flex min-w-0 flex-col gap-4'>
            <h3 className='text-xs font-semibold uppercase tracking-wide text-base-content/80'>
                Детализация по критериям
            </h3>
            {mergedBlocks.map(block => {
                const topBorder = BLOCK_TOP_BORDER[block.blockType]
                const sectionTitle = getBlockTitle(block.blockType, block.title)

                const handleExport = () => {
                    exportBlockCriteriaToExcel(block.criterias, sectionTitle, brandA.name, brandB.name, block.blockType)
                }

                return (
                    <Card key={block.blockType} color='primary' topBorderColor={topBorder} tableScroll>
                        <div className='mb-4 flex items-center justify-between gap-4'>
                            <h4
                                className={cn(
                                    'text-xs font-bold uppercase',
                                    topBorder ? BLOCK_TITLE_TEXT_CLASS[topBorder] : 'text-base-content'
                                )}
                            >
                                {sectionTitle}
                            </h4>
                            <CompanyStatsExcelExportButton
                                onClick={handleExport}
                                disabled={block.criterias.length === 0}
                                ariaLabel={`Экспорт ${sectionTitle} в Excel`}
                            />
                        </div>
                        <TableScrollHost minHeight='8rem' outline={false}>
                            <Table pinRows striped thBg rounded={false} className='w-full min-w-[40rem]'>
                                <Thead>
                                    <Tr>
                                        <Th>Критерий</Th>
                                        <Th className='w-24 text-center'>Разрыв</Th>
                                        <Th className='w-28 text-center text-[#08AC81]'>{brandA.name}</Th>
                                        <Th className='w-28 text-center text-[#8b5cf6]'>{brandB.name}</Th>
                                        <Th className='w-36 text-center'>Лидер</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {block.criterias.map(row => (
                                        <Tr key={row.criteriaName}>
                                            <Td className='text-sm'>{row.label}</Td>
                                            <Td className='text-center'>
                                                {row.delta.sign === 'equal' ? (
                                                    <span className='text-base-content/40'>≈</span>
                                                ) : (
                                                    <BrandComparisonDeltaBadge delta={row.delta} variant='gap' />
                                                )}
                                            </Td>
                                            <Td className='text-center text-sm font-medium tabular-nums text-[#08AC81]'>
                                                {Math.round(row.brandAPercent)}%
                                            </Td>
                                            <Td className='text-center text-sm font-medium tabular-nums text-[#8b5cf6]'>
                                                {Math.round(row.brandBPercent)}%
                                            </Td>
                                            <Td className='text-center text-sm'>
                                                {row.leader.type === 'equal' ? (
                                                    <span className='text-base-content/40'>≈</span>
                                                ) : (
                                                    <span className='text-[#08AC81]'>↑ {row.leader.name}</span>
                                                )}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableScrollHost>
                    </Card>
                )
            })}
        </div>
    )
}
